import {
  resolveKeyDates,
  trimSchedulesToHorizon,
  untieKeyDate,
  type Assumptions,
  type Category,
  type CategoryId,
  type Goal,
  type GoalId,
  type Investment,
  type InvestmentId,
  type Item,
  type ItemId,
  type KeyDate,
  type KeyDateId,
  type Project,
  type Scenario,
  type ScenarioId,
} from '@finsim/model';

/**
 * Every edit to a project is a command applied by the pure `applyCommand` reducer. Commands carry
 * any generated ids so the reducer stays deterministic and unit-testable without React or a DOM.
 */
export type Command =
  | { type: 'project/rename'; name: string }
  /** Shortening the horizon also trims schedule periods that would start after it. */
  | { type: 'assumptions/update'; assumptions: Assumptions }
  | { type: 'category/add'; category: Category }
  | { type: 'category/update'; category: Category }
  /** Refused while any item uses the category. */
  | { type: 'category/remove'; id: CategoryId }
  | { type: 'goal/add'; goal: Goal }
  | { type: 'goal/update'; goal: Goal }
  | { type: 'goal/remove'; id: GoalId }
  /** `items` are tagged with it (one undo step). */
  | { type: 'investment/add'; investment: Investment; items?: readonly ItemId[] }
  | { type: 'investment/update'; investment: Investment }
  /** Also untags its items. */
  | { type: 'investment/remove'; id: InvestmentId }
  /** Moving a key date moves every month tied to it. */
  | { type: 'keyDate/add'; keyDate: KeyDate }
  | { type: 'keyDate/update'; keyDate: KeyDate }
  /** Months tied to it stay where they are, untied. */
  | { type: 'keyDate/remove'; id: KeyDateId }
  | { type: 'scenario/add'; scenario: Scenario }
  | { type: 'scenario/update'; id: ScenarioId; patch: Partial<Pick<Scenario, 'name' | 'color'>> }
  | { type: 'scenario/remove'; id: ScenarioId }
  | { type: 'scenario/setBaseline'; id: ScenarioId }
  /**
   * New variant based on `sourceId`. Items listed in `copies` (old → new id) are duplicated;
   * every other item is shared with the source.
   */
  | {
      type: 'scenario/duplicate';
      sourceId: ScenarioId;
      scenario: Omit<Scenario, 'itemIds'>;
      copies: Record<ItemId, ItemId>;
    }
  | { type: 'item/add'; item: Item; scenarioId: ScenarioId }
  | { type: 'item/update'; item: Item }
  /** Removes the item from one variant (or all when `scenarioId` is null). */
  | { type: 'item/remove'; id: ItemId; scenarioId: ScenarioId | null }
  | { type: 'item/share'; id: ItemId; scenarioId: ScenarioId }
  /** Replaces a shared item in one variant with an independent copy. */
  | { type: 'item/detach'; id: ItemId; scenarioId: ScenarioId; newId: ItemId }
  | { type: 'item/duplicate'; id: ItemId; scenarioId: ScenarioId; newId: ItemId; name: string };

export class CommandError extends Error {
  constructor(
    readonly code: 'notFound' | 'invalid' | 'inUse',
    message: string,
  ) {
    super(message);
  }
}

function requireItem(project: Project, id: ItemId): Item {
  const item = project.items[id];
  if (!item) throw new CommandError('notFound', `Unknown item ${id}`);
  return item;
}

function requireScenario(project: Project, id: ScenarioId): Scenario {
  const scenario = project.scenarios.find((s) => s.id === id);
  if (!scenario) throw new CommandError('notFound', `Unknown variant ${id}`);
  return scenario;
}

const updateScenario = (
  project: Project,
  id: ScenarioId,
  fn: (s: Scenario) => Scenario,
): Scenario[] => project.scenarios.map((s) => (s.id === id ? fn(s) : s));

/** Rewrites item references (formula sources, transfer targets) through `map`. */
export function remapReferences(item: Item, map: ReadonlyMap<ItemId, ItemId>): Item {
  if (item.type !== 'flow' && item.type !== 'formula') return item;
  let next: Item = item;
  const account = item.account === undefined ? undefined : map.get(item.account);
  if (account !== undefined) next = { ...item, account };
  if (next.type === 'formula') {
    const sources = next.spec.sources.map((s) =>
      s.ref.type === 'item' && map.has(s.ref.id)
        ? { ...s, ref: { type: 'item' as const, id: map.get(s.ref.id) as ItemId } }
        : s,
    );
    next = { ...next, spec: { ...next.spec, sources } };
  }
  return next;
}

/**
 * Deletes items from the project, cascading to what cannot exist without them: transfers into a
 * deleted account are deleted too, and formula sources pointing at deleted items are dropped.
 */
export function deleteItems(project: Project, ids: Iterable<ItemId>): Project {
  const doomed = new Set(ids);
  let grew = true;
  while (grew) {
    grew = false;
    for (const item of Object.values(project.items)) {
      if (doomed.has(item.id)) continue;
      if (
        (item.type === 'flow' || item.type === 'formula') &&
        item.account !== undefined &&
        doomed.has(item.account)
      ) {
        doomed.add(item.id);
        grew = true;
      }
    }
  }
  const items: Record<string, Item> = {};
  for (const [key, item] of Object.entries(project.items)) {
    if (doomed.has(item.id)) continue;
    items[key] =
      item.type === 'formula' &&
      item.spec.sources.some((s) => s.ref.type === 'item' && doomed.has(s.ref.id))
        ? {
            ...item,
            spec: {
              ...item.spec,
              sources: item.spec.sources.filter(
                (s) => !(s.ref.type === 'item' && doomed.has(s.ref.id)),
              ),
            },
          }
        : item;
  }
  return {
    ...project,
    items,
    scenarios: project.scenarios.map((s) =>
      s.itemIds.some((id) => doomed.has(id))
        ? { ...s, itemIds: s.itemIds.filter((id) => !doomed.has(id)) }
        : s,
    ),
  };
}

/** Items referenced by no variant. */
function orphans(project: Project): ItemId[] {
  const used = new Set(project.scenarios.flatMap((s) => s.itemIds));
  return Object.values(project.items)
    .map((i) => i.id)
    .filter((id) => !used.has(id));
}

/** Applies `command`, then moves every month tied to a key date to match it. */
export function applyCommand(project: Project, command: Command): Project {
  return resolveKeyDates(reduce(project, command));
}

function reduce(project: Project, command: Command): Project {
  switch (command.type) {
    case 'project/rename':
      return { ...project, name: command.name };

    case 'assumptions/update': {
      const next = { ...project, assumptions: command.assumptions };
      return command.assumptions.horizonMonths < project.assumptions.horizonMonths
        ? trimSchedulesToHorizon(next, command.assumptions.horizonMonths)
        : next;
    }

    case 'category/add':
      return { ...project, categories: [...project.categories, command.category] };

    case 'category/update':
      if (!project.categories.some((c) => c.id === command.category.id)) {
        throw new CommandError('notFound', `Unknown category ${command.category.id}`);
      }
      return {
        ...project,
        categories: project.categories.map((c) =>
          c.id === command.category.id ? command.category : c,
        ),
      };

    case 'category/remove': {
      const inUse = Object.values(project.items).some(
        (i) =>
          (i.type !== 'asset' && i.category === command.id) ||
          (i.type === 'formula' &&
            i.spec.sources.some((s) => s.ref.type === 'category' && s.ref.id === command.id)),
      );
      if (inUse) throw new CommandError('inUse', `Category ${command.id} is in use`);
      return { ...project, categories: project.categories.filter((c) => c.id !== command.id) };
    }

    case 'goal/add':
      return { ...project, goals: [...project.goals, command.goal] };

    case 'goal/update':
      if (!project.goals.some((g) => g.id === command.goal.id)) {
        throw new CommandError('notFound', `Unknown goal ${command.goal.id}`);
      }
      return {
        ...project,
        goals: project.goals.map((g) => (g.id === command.goal.id ? command.goal : g)),
      };

    case 'goal/remove':
      return { ...project, goals: project.goals.filter((g) => g.id !== command.id) };

    case 'investment/add': {
      const items = { ...project.items };
      for (const id of command.items ?? []) {
        const item = items[id];
        if (!item) throw new CommandError('notFound', `Unknown item ${id}`);
        items[id] = { ...item, investment: command.investment.id };
      }
      return { ...project, items, investments: [...project.investments, command.investment] };
    }

    case 'investment/update':
      if (!project.investments.some((i) => i.id === command.investment.id)) {
        throw new CommandError('notFound', `Unknown investment ${command.investment.id}`);
      }
      return {
        ...project,
        investments: project.investments.map((i) =>
          i.id === command.investment.id ? command.investment : i,
        ),
      };

    case 'investment/remove': {
      const items: Record<string, Item> = {};
      for (const [key, item] of Object.entries(project.items)) {
        if (item.investment === command.id) {
          const { investment: _, ...untagged } = item;
          items[key] = untagged;
        } else {
          items[key] = item;
        }
      }
      return {
        ...project,
        items,
        investments: project.investments.filter((i) => i.id !== command.id),
      };
    }

    case 'keyDate/add':
      return { ...project, keyDates: [...project.keyDates, command.keyDate] };

    case 'keyDate/update':
      if (!project.keyDates.some((k) => k.id === command.keyDate.id)) {
        throw new CommandError('notFound', `Unknown key date ${command.keyDate.id}`);
      }
      return {
        ...project,
        keyDates: project.keyDates.map((k) => (k.id === command.keyDate.id ? command.keyDate : k)),
      };

    case 'keyDate/remove':
      return untieKeyDate(
        { ...project, keyDates: project.keyDates.filter((k) => k.id !== command.id) },
        command.id,
      );

    case 'scenario/add':
      return {
        ...project,
        scenarios: [...project.scenarios, command.scenario],
        baselineScenarioId: project.baselineScenarioId ?? command.scenario.id,
      };

    case 'scenario/update':
      requireScenario(project, command.id);
      return {
        ...project,
        scenarios: updateScenario(project, command.id, (s) => ({ ...s, ...command.patch })),
      };

    case 'scenario/setBaseline':
      requireScenario(project, command.id);
      return { ...project, baselineScenarioId: command.id };

    case 'scenario/remove': {
      requireScenario(project, command.id);
      const scenarios = project.scenarios.filter((s) => s.id !== command.id);
      const next: Project = {
        ...project,
        scenarios,
        baselineScenarioId:
          project.baselineScenarioId === command.id
            ? (scenarios[0]?.id ?? null)
            : project.baselineScenarioId,
      };
      return deleteItems(next, orphans(next));
    }

    case 'scenario/duplicate': {
      const source = requireScenario(project, command.sourceId);
      const map = new Map(Object.entries(command.copies) as [ItemId, ItemId][]);
      const items = { ...project.items };
      for (const [oldId, newId] of map) {
        const original = requireItem(project, oldId);
        items[newId] = remapReferences({ ...original, id: newId }, map);
      }
      const scenario: Scenario = {
        ...command.scenario,
        itemIds: source.itemIds.map((id) => map.get(id) ?? id),
      };
      const index = project.scenarios.findIndex((s) => s.id === source.id);
      const scenarios = [...project.scenarios];
      scenarios.splice(index + 1, 0, scenario);
      return { ...project, items, scenarios };
    }

    case 'item/add':
      requireScenario(project, command.scenarioId);
      return {
        ...project,
        items: { ...project.items, [command.item.id]: command.item },
        scenarios: updateScenario(project, command.scenarioId, (s) => ({
          ...s,
          itemIds: [...s.itemIds, command.item.id],
        })),
      };

    case 'item/update':
      requireItem(project, command.item.id);
      return { ...project, items: { ...project.items, [command.item.id]: command.item } };

    case 'item/remove': {
      requireItem(project, command.id);
      if (command.scenarioId === null) return deleteItems(project, [command.id]);
      const next: Project = {
        ...project,
        scenarios: updateScenario(project, command.scenarioId, (s) => ({
          ...s,
          itemIds: s.itemIds.filter((id) => id !== command.id),
        })),
      };
      return deleteItems(next, orphans(next));
    }

    case 'item/share': {
      requireItem(project, command.id);
      const scenario = requireScenario(project, command.scenarioId);
      if (scenario.itemIds.includes(command.id)) return project;
      return {
        ...project,
        scenarios: updateScenario(project, command.scenarioId, (s) => ({
          ...s,
          itemIds: [...s.itemIds, command.id],
        })),
      };
    }

    case 'item/detach':
    case 'item/duplicate': {
      const original = requireItem(project, command.id);
      const scenario = requireScenario(project, command.scenarioId);
      const copy: Item =
        command.type === 'item/duplicate'
          ? { ...original, id: command.newId, name: command.name }
          : { ...original, id: command.newId };
      const items: Record<string, Item> = { ...project.items, [copy.id]: copy };
      let itemIds: ItemId[];
      if (command.type === 'item/duplicate') {
        const at = scenario.itemIds.indexOf(command.id);
        itemIds = [...scenario.itemIds];
        itemIds.splice(at + 1, 0, copy.id);
      } else {
        itemIds = scenario.itemIds.map((id) => (id === command.id ? copy.id : id));
        // Items only this variant uses follow the detached copy (e.g. its transfers and formulas).
        const map = new Map([[command.id, command.newId]]);
        const usage = usageCount(project);
        for (const id of scenario.itemIds) {
          const item = project.items[id];
          if (item && id !== command.id && usage.get(id) === 1)
            items[id] = remapReferences(item, map);
        }
      }
      return {
        ...project,
        items,
        scenarios: updateScenario(project, command.scenarioId, (s) => ({ ...s, itemIds })),
      };
    }
  }
}

/** How many variants reference each item. */
export function usageCount(project: Project): Map<ItemId, number> {
  const counts = new Map<ItemId, number>();
  for (const s of project.scenarios)
    for (const id of s.itemIds) counts.set(id, (counts.get(id) ?? 0) + 1);
  return counts;
}
